import { intersect, subtract, unify } from '@flatten-js/boolean-op';
import { Line, ORIENTATION, Point, Polygon, Segment, Vector } from '@flatten-js/core';
import {
  constrainSegmentToBorders,
  getMainPolygonFaceIndex,
  isInnerBorder,
  isInnerEdge,
  orderPointsBelongingToLine,
} from './calc-auto-breakerboard';

export function createPolygonsFromFaces(polygon, polyMainOrientation) {
  const polygonsWithoutHoles = [];
  const holes = [];
  [...polygon.faces].forEach(face => {
    const newPoly = new Polygon();
    newPoly.addFace(face);
    if (face.orientation() === polyMainOrientation) {
      polygonsWithoutHoles.push(newPoly);
    } else {
      holes.push(newPoly);
    }
  });

  return createPolygonsFromIntersections(polygonsWithoutHoles, holes);
}

export function createPolygonsFromIntersections(polygonsWithoutHoles, holes) {
  const polygons = polygonsWithoutHoles;
  holes.forEach(holePoly => {
    for (let i = 0; i < polygons.length; i++) {
      const polyWithHole = subtract(polygons[i], holePoly);
      if (polygons[i].faces.size < polyWithHole.faces.size) {
        polygons[i] = polyWithHole;
        break;
      }
    }
  });
  return polygons;
}

/**
 * Trapezoid is created from original segment, and the other parallel segment is displaced 'width"
 * in CW 90 degrees from the original segment.
 *
 * @export
 * @param {Flatten.Segment} segment
 * @param {number} width
 * @param {Flatten.Polygon} [polygonFit=null]
 * @returns {Flatten.Polygon}
 */
export function trapezoidFromSide(segment, width, polygonFit = null) {
  const displacementVector = new Vector(segment.ps, segment.pe)
    .rotate90CW()
    .normalize()
    .multiply(width);
  const startVector = new Vector(segment.ps.x, segment.ps.y);
  const endVector = new Vector(segment.pe.x, segment.pe.y);
  const parallelPointVectors = [endVector.add(displacementVector), startVector.add(displacementVector)];
  const parallelSegment = new Segment(
    new Point(parallelPointVectors[0].x, parallelPointVectors[0].y),
    new Point(parallelPointVectors[1].x, parallelPointVectors[1].y)
  );
  let vertices;
  if (polygonFit) {
    const parallelSegmentsExt = constrainSegmentToBorders(parallelSegment, polygonFit);
    const segmentsExtPoints = [];
    parallelSegmentsExt.forEach(segment => segmentsExtPoints.push(...segment.vertices));
    orderPointsBelongingToLine(segmentsExtPoints);
    // flip trapezoid segmentsExtPoints initial and end points if withSides cross (would produce wrong polygon)
    const widthSideA = new Segment(segment.ps, segmentsExtPoints[0]);
    const widthSideB = new Segment(segment.pe, segmentsExtPoints.slice(-1)[0]);
    if (widthSideA.intersect(widthSideB).length === 0) {
      vertices = [segment.ps, segment.pe, segmentsExtPoints.slice(-1)[0], segmentsExtPoints[0]];
    } else {
      vertices = [segment.ps, segment.pe, segmentsExtPoints[0], segmentsExtPoints.slice(-1)[0]];
    }
  } else {
    vertices = [segment.ps, segment.pe, parallelSegment.ps, parallelSegment.pe];
  }
  const polygon = new Polygon();
  polygon.addFace(vertices);
  return polygon;
}

/**
 * Creates trapezoids from edges. Trapezoids are breakerboards with on of their sides equal
 * to the edge of the area. The other parallel side is located at a breakerboardWidth distance.
 *  - If the edge is not adjacent to other edges with breakerboards, this parallel line ends on
 * the intersection of the extended lines from the previous and next edge vectors.
 *  - If the edge is adjacent to other edges with breakerboards, this parallel line ends on
 * the intersection of the extended lines from the previous and next edge border OR parallel border,
 * whatever is required to guarantee they overlap.
 * @export
 * @param {{segment: Flatten.Segment, breakerboardWidth: number}[]} breakerboardEdges - Edges from which to create breakerboards.
 * @param {Flatten.Vector|null} prevVector - Previous vector, calculated out of the edge before the first edge in breakerboardEdges.
 * @param {Flatten.Vector|null} nextVector - Next vector, calculated out of the edge after the last edge in breakerboardEdges.
 * @returns {Flatten.Polygon[]} - Array of breakerboards.
 */
export function trapezoidsFromVectors(breakerboardEdges, prevVector, nextVector) {
  const sidesLines = breakerboardEdges.map(edge => new Line(edge.segment.ps, edge.segment.pe));
  const sidesParallelLines = breakerboardEdges.map(breakerboardEdge => {
    const segment = breakerboardEdge.segment;
    const displacementVector = new Vector(segment.ps, segment.pe)
      .rotate90CW()
      .normalize()
      .multiply(breakerboardEdge.breakerboardWidth);
    const startVector = new Vector(segment.ps.x, segment.ps.y);
    const endVector = new Vector(segment.pe.x, segment.pe.y);
    const parallelPointVectors = [endVector.add(displacementVector), startVector.add(displacementVector)];
    return new Line(
      new Point(parallelPointVectors[0].x, parallelPointVectors[0].y),
      new Point(parallelPointVectors[1].x, parallelPointVectors[1].y)
    );
  });
  const lines = [...sidesLines];
  const parallelLines = [...sidesParallelLines];
  if (prevVector === null && nextVector === null) {
    // closed shape, prev and next vector match with prev and next sides
    lines.unshift(sidesLines.slice(-1)[0]);
    lines.push(sidesLines[0]);
    parallelLines.unshift(sidesParallelLines.slice(-1)[0]);
    parallelLines.push(sidesParallelLines[0]);
  } else {
    const prevSideLine = new Line(breakerboardEdges[0].segment.ps, prevVector.rotate90CW());
    const nextSideLine = new Line(breakerboardEdges.slice(-1)[0].segment.pe, nextVector.rotate90CW());
    lines.unshift(prevSideLine);
    lines.push(nextSideLine);
    parallelLines.unshift(prevSideLine);
    parallelLines.push(nextSideLine);
  }

  const trapezoids = [];
  for (let i = 1; i < parallelLines.length - 1; i++) {
    const prevLine = lines[i - 1];
    const prevParallelLine = parallelLines[i - 1];
    const parallelLine = parallelLines[i];
    const line = lines[i];
    const nextLine = lines[i + 1];
    const nextParallelLine = parallelLines[i + 1];
    const prevVertexCandidates = [line.intersect(prevLine)[0], line.intersect(prevParallelLine)[0]];
    const nextVertexCandidates = [line.intersect(nextLine)[0], line.intersect(nextParallelLine)[0]];
    const [prevVertex, nextVertex] = furthestPoints(prevVertexCandidates, nextVertexCandidates);
    const prevParallelVertexCandidates = [
      parallelLine.intersect(prevParallelLine)[0],
      parallelLine.intersect(prevLine)[0],
    ];
    const nextParallelVertexCandidates = [
      parallelLine.intersect(nextParallelLine)[0],
      parallelLine.intersect(nextLine)[0],
    ];
    const [prevParallelVertex, nextParallelVertex] = furthestPoints(
      prevParallelVertexCandidates,
      nextParallelVertexCandidates
    );
    const trapezoid = new Polygon();
    trapezoid.addFace([prevVertex, nextVertex, nextParallelVertex, prevParallelVertex]);
    trapezoids.push(trapezoid);
  }
  return trapezoids;
}

function furthestPoints(prevCandidatePoints, nextCandidatePoints) {
  let nextPoint;
  if (
    prevCandidatePoints[0].distanceTo(nextCandidatePoints[0])[0] >
    prevCandidatePoints[0].distanceTo(nextCandidatePoints[1])[0]
  ) {
    nextPoint = nextCandidatePoints[0];
  } else {
    nextPoint = nextCandidatePoints[1];
  }
  let prevPoint;
  if (
    nextCandidatePoints[0].distanceTo(prevCandidatePoints[0])[0] >
    nextCandidatePoints[0].distanceTo(prevCandidatePoints[1])[0]
  ) {
    prevPoint = prevCandidatePoints[0];
  } else {
    prevPoint = prevCandidatePoints[1];
  }
  return [prevPoint, nextPoint];
}

export function trapezoidFromCenter(segment, width, polygonFit = null) {
  const breakerboardVector = new Vector(segment.ps, segment.pe);
  const startVector = new Vector(segment.ps.x, segment.ps.y);
  const endVector = new Vector(segment.pe.x, segment.pe.y);
  const displacement = breakerboardVector
    .rotate90CW()
    .normalize()
    .multiply(width / 2);

  // create long Breakerboard segments by displacing the center of the Breakerboard and constraining them to polygon edges
  const breakerboardLongSegments = [
    [startVector.add(displacement), endVector.add(displacement)],
    [endVector.subtract(displacement), startVector.subtract(displacement)],
  ].map(segmentVertices => {
    const segment = new Segment(
      new Point(segmentVertices[0].x, segmentVertices[0].y),
      new Point(segmentVertices[1].x, segmentVertices[1].y)
    );

    if (polygonFit) {
      return constrainSegmentToBorders(segment, polygonFit)[0];
    } else {
      return segment;
    }
  });
  const cutVertices = [];
  breakerboardLongSegments.forEach(segment => cutVertices.push(...[segment.ps, segment.pe]));

  const breakerboardCutArea = new Polygon();
  breakerboardCutArea.addFace(cutVertices);
  return breakerboardCutArea;
}

/**
 * Adds a Breakerboard in the middle of two areas, and shrinks them accordingly.
 * The Breakerboard center will fall on the given breakerboardMiddleSegment, which should be the shared areas border.
 * @param {Flatten.Polygon[]} areas - One or more areas with a single shared border.
 * @param {Flatten.Segment} breakerboardMiddleSegment - Single shared border where Breakerboard will be placed.
 * @param {number} breakerboardWidth - Width of the Breakerboard.
 * @returns {[Flatten.Polygon[], Flatten.Polygon[]]} - Array of shrunk areas, array of created Breakerboard.
 */
export function addBreakerboardMiddleAreas(areas, breakerboardMiddleSegment, breakerboardWidth) {
  let unifiedPoly = areas[0];
  for (const area of areas.slice(1)) {
    unifiedPoly = unify(unifiedPoly, area);
  }

  const outerFaceIndex = getMainPolygonFaceIndex(unifiedPoly);

  const polyMainOrientation = [...unifiedPoly.faces][outerFaceIndex].orientation();
  // secondary faces will be considered holes
  [...unifiedPoly.faces].forEach((face, i) => {
    if (i !== outerFaceIndex && face.orientation() === polyMainOrientation) {
      face.reverse();
    }
  });
  const unifiedPolyNoHoles = new Polygon();
  unifiedPolyNoHoles.addFace([...unifiedPoly.faces][outerFaceIndex]);

  const breakerboardCutArea = trapezoidFromCenter(breakerboardMiddleSegment, breakerboardWidth, unifiedPolyNoHoles);
  if ([...breakerboardCutArea.faces][0].orientation() !== polyMainOrientation) {
    [...breakerboardCutArea.faces][0].reverse();
  }

  const breakerboardArea = intersect(unifiedPoly, breakerboardCutArea);
  const reducedAreas = subtract(unifiedPoly, breakerboardCutArea);

  return [
    createPolygonsFromFaces(reducedAreas, polyMainOrientation),
    createPolygonsFromFaces(breakerboardArea, polyMainOrientation),
  ];
}

function enforceSameOrientation(breakerboardEdges, polygon, outerFaceIndex) {
  // enforce all edges have same orientation as polygon
  const polygonEdges = [...polygon.faces][outerFaceIndex].edges.map(polyEdge => polyEdge.shape);
  const edgesToFind = breakerboardEdges.map(breakerboardEdge => {
    if (polygonEdges.some(polygonEdge => polygonEdge.equalTo(breakerboardEdge.segment))) {
      return breakerboardEdge;
    } else if (polygonEdges.some(polygonEdge => polygonEdge.equalTo(breakerboardEdge.segment.reverse()))) {
      return { segment: breakerboardEdge.segment.reverse(), breakerboardWidth: breakerboardEdge.breakerboardWidth };
    } else {
      throw new Error(`Edge ${breakerboardEdge.ps}:${breakerboardEdge.pe} doesn't belong to polygon`);
    }
  });
  return edgesToFind;
}

/**
 * Adds breakerboards in one or more edges of an area, and shrinks the area accordingly. Breakerboards are placed
 * in the inner side of the area, so the resulting area will always be smaller.
 *
 * The algorithm iterates over all the polygon edges, checking if they are in breakerboardEdges, therefore meaning
 * breakerboards should be created.
 *  - If its an outer line, a breakerboard is created (extending its sides) so it covers non-rectangular edge angles.
 *  - If its an inner line, first it checks if there are consecutive inner lines which should become breakerboards.
 *       These need to be processed together, as they might need to be extended to overlap. Inner line breakerboards
 *       are cut using their previous and next edges extended lines as limits, to preserve the area number of edges.
 *
 * @param {Flatten.Polygon} area - The area to add a breakerboard to.
 * @param {{segment: Flatten.Segment, breakerboardWidth: number}[]} breakerboardEdges - Array containing the edges
 * (segments) on which the breakerboards will be placed, with their corresponding breakerboard width. Each side can have
 * a different breakerboard width. Edges must match exactly the edges of the polygon.
 * @returns {[Flatten.Polygon[], Flatten.Polygon[]]} array of shrunk areas, array of created breakerboards in the order of
 * the area orientation. THe order is guaranteed to match the breakerboardEdges order under the condition breakerboardEdges
 * contains a single group of inner line edges, with the same orientation as the area.
 */
export function addBreakerboardAreaSide(area, breakerboardEdges) {
  const outerFaceIndex = getMainPolygonFaceIndex(area);
  const polyMainOrientation = [...area.faces][outerFaceIndex].orientation();
  // secondary faces will be considered holes
  [...area.faces].forEach((face, i) => {
    if (i !== outerFaceIndex && face.orientation() === polyMainOrientation) {
      face.reverse();
    }
  });

  // enforce all edges have same orientation as polygon
  let edgesToFind = enforceSameOrientation(breakerboardEdges, area, outerFaceIndex);

  const cutters = [];
  [...area.faces][outerFaceIndex].edges.forEach(polyEdge => {
    if (edgesToFind.some(breakerboardEdge => polyEdge.shape.equalTo(breakerboardEdge.segment))) {
      let foundEdge = edgesToFind.filter(breakerboardEdge => polyEdge.shape.equalTo(breakerboardEdge.segment))[0];
      // remove found edge
      edgesToFind = edgesToFind.filter(breakerboardEdge => !polyEdge.shape.equalTo(breakerboardEdge.segment));
      let cutter;
      const edgeVector = new Vector(polyEdge.shape.ps, polyEdge.shape.pe);
      let prevVector = new Vector(polyEdge.prev.shape.ps, polyEdge.prev.shape.pe);
      let nextVector = new Vector(polyEdge.next.shape.ps, polyEdge.next.shape.pe);
      if (isInnerBorder(edgeVector, prevVector, nextVector, polyMainOrientation === ORIENTATION.CW)) {
        // inner border breaker boards need to be extended, if next to other inner borders with breaker boards
        let innerEdgesToProcess = [{ segment: polyEdge.shape, breakerboardWidth: foundEdge.breakerboardWidth }];
        let prevEdge = polyEdge.prev;
        // back search for consecutive inner borders
        while (
          edgesToFind.some(breakerboardEdge => prevEdge.shape.equalTo(breakerboardEdge.segment)) &&
          isInnerEdge(prevEdge, polyMainOrientation === ORIENTATION.CW)
        ) {
          foundEdge = edgesToFind.filter(breakerboardEdge => prevEdge.shape.equalTo(breakerboardEdge.segment))[0];
          edgesToFind = edgesToFind.filter(breakerboardEdge => !prevEdge.shape.equalTo(breakerboardEdge.segment));
          innerEdgesToProcess.unshift({ segment: prevEdge.shape, breakerboardWidth: foundEdge.breakerboardWidth });
          prevEdge = prevEdge.prev;
          prevVector = new Vector(prevEdge.shape.ps, prevEdge.shape.pe);
        }
        let nextEdge = polyEdge.next;
        // forward search for consecutive inner borders
        while (
          edgesToFind.some(breakerboardEdge => nextEdge.shape.equalTo(breakerboardEdge.segment)) &&
          isInnerEdge(nextEdge, polyMainOrientation === ORIENTATION.CW)
        ) {
          foundEdge = edgesToFind.filter(breakerboardEdge => nextEdge.shape.equalTo(breakerboardEdge.segment))[0];
          edgesToFind = edgesToFind.filter(breakerboardEdge => !nextEdge.shape.equalTo(breakerboardEdge.segment));
          innerEdgesToProcess.push({ segment: nextEdge.shape, breakerboardWidth: foundEdge.breakerboardWidth });
          nextEdge = nextEdge.next;
          nextVector = new Vector(nextEdge.shape.ps, nextEdge.shape.pe);
        }
        // if inner borders close polygon the whole polygon (ex. star-shape)
        if (innerEdgesToProcess.length === [...area.faces][outerFaceIndex].edges.length) {
          // do not use prev and next vector
          prevVector = null;
          nextVector = null;
          // ensure first inner edge is first polygon edge
          const startEdgeIndex = innerEdgesToProcess.findIndex(edge =>
            edge.segment.equalTo(breakerboardEdges[0].segment)
          );
          innerEdgesToProcess = [
            ...innerEdgesToProcess.slice(startEdgeIndex),
            ...innerEdgesToProcess.slice(0, startEdgeIndex),
          ];
        }
        // same segments orientation if CW. opposite if CCW
        if (polyMainOrientation === ORIENTATION.CW) {
          cutter = trapezoidsFromVectors(innerEdgesToProcess, prevVector, nextVector);
        } else {
          cutter = trapezoidsFromVectors(
            innerEdgesToProcess
              .map(breakerboardEdge => ({
                segment: breakerboardEdge.segment.reverse(),
                breakerboardWidth: breakerboardEdge.breakerboardWidth,
              }))
              .reverse(),
            nextVector,
            prevVector
          );
          cutter.reverse();
        }
      } else {
        // outer borders don't need to take into account if other borders have breaker boards (they superpose by default)
        // same segment orientation if CW. opposite if CCW
        if (polyMainOrientation === ORIENTATION.CW) {
          cutter = [trapezoidFromSide(polyEdge.shape, foundEdge.breakerboardWidth, area)];
        } else {
          cutter = [trapezoidFromSide(polyEdge.shape.reverse(), foundEdge.breakerboardWidth, area)];
        }
      }
      cutters.push(...cutter);
    }
  });

  let unitedCutter;
  for (const cutter of cutters) {
    if ([...cutter.faces][0].orientation() !== polyMainOrientation) {
      [...cutter.faces][0].reverse();
    }
    if (unitedCutter === undefined) {
      unitedCutter = cutter;
    } else {
      unitedCutter = unify(unitedCutter, cutter);
    }
  }

  const reducedAreas = subtract(area, unitedCutter);
  return [createPolygonsFromFaces(reducedAreas, polyMainOrientation), cutters];
}
